// ApiError — discriminated union surfaced by every @fluxora/api-client call.
//
// Spec: Epic FE-US-INT-01 §4.5.
//
// UI layer maps each `kind` to a user-friendly toast/banner. Components must
// pattern-match on `kind` — narrowing into the union gives full type safety
// for the additional fields per variant.
//
// We keep this dependency-light: `ProblemDetails` is shaped loosely so that
// callers can plug their own narrower schema in without us pulling Zod into
// this file. The createApiClient layer is responsible for parsing.

import type { ZodError } from 'zod';

/**
 * RFC 7807 Problem Details. Backend services emit a superset of this shape;
 * we only require the canonical fields and pass through the rest.
 */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  code?: string;
  correlationId?: string;
  // Extension members per RFC 7807 §3.2.
  [key: string]: unknown;
}

export type ApiError =
  | { kind: 'network'; message: string }
  | { kind: 'timeout'; message: string; timeoutMs: number }
  | { kind: 'auth'; status: 401 | 403; problem?: ProblemDetails }
  | { kind: 'client'; status: number; problem: ProblemDetails }
  | { kind: 'server'; status: number; problem?: ProblemDetails }
  | { kind: 'validation'; zodError: ZodError };

/** Type-guard: useful in mutation `onError` and TanStack Query `select`. */
export function isApiError(value: unknown): value is ApiError {
  if (value === null || typeof value !== 'object') return false;
  const k = (value as { kind?: unknown }).kind;
  return (
    k === 'network' ||
    k === 'timeout' ||
    k === 'auth' ||
    k === 'client' ||
    k === 'server' ||
    k === 'validation'
  );
}

/**
 * Map an ApiError to a short, user-facing message string. Intentionally
 * generic — module-specific copy should live in i18n keys.
 */
export function describeApiError(err: ApiError): string {
  switch (err.kind) {
    case 'network':
      return err.message || 'Network error. Check your connection and try again.';
    case 'timeout':
      return `Request timed out after ${err.timeoutMs}ms. Try again in a moment.`;
    case 'auth':
      return err.problem?.detail ?? 'You are not authorized to perform this action.';
    case 'client':
      return err.problem.detail ?? err.problem.title ?? 'The request was invalid.';
    case 'server':
      return err.problem?.detail ?? 'The server is unavailable. Try again shortly.';
    case 'validation':
      return 'The server returned data the app could not understand.';
  }
}
